/**
 * DealContext — Global state for deals, active DSR, and guided chips.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import * as api from "@/api/client";
import type { DealData } from "@/api/client";
import type { PromptChip } from "@/schemas/deals";

interface DealContextValue {
  deals: DealData[];
  activeDeal: DealData | null;
  activeDSR: Record<string, unknown> | null;
  chips: PromptChip[];
  loading: boolean;
  refreshDeals: () => Promise<void>;
  setActiveDeal: (deal: DealData | null) => void;
  setActiveDSR: (dsr: Record<string, unknown> | null) => void;
  createDeal: (params: Parameters<typeof api.createDeal>[0]) => Promise<DealData | null>;
  updateDeal: (id: string, fields: Partial<Pick<DealData, "status" | "title" | "notes" | "intended_use" | "property_type" | "county">>) => Promise<DealData | null>;
  handleDealContext: (event: {
    deal_id: string;
    parcel_id?: string;
    address?: string;
    status: string;
    title?: string;
    completeness_score: number;
  }) => void;
  handleDSRUpdate: (event: {
    deal_id: string;
    completeness_score: number;
    pir_status: Record<string, string>;
    ground_truth: Record<string, unknown>;
    estimates: Record<string, unknown>;
  }) => void;
  handleChipsUpdate: (chips: Array<{ label: string; message: string; category: string }>) => void;
}

const DealCtx = createContext<DealContextValue | null>(null);

export function useDeal(): DealContextValue {
  const ctx = useContext(DealCtx);
  if (!ctx) throw new Error("useDeal must be used within DealProvider");
  return ctx;
}

export function DealProvider({ children }: { children: ReactNode }) {
  const [deals, setDeals] = useState<DealData[]>([]);
  const [activeDeal, setActiveDeal] = useState<DealData | null>(null);
  const [activeDSR, setActiveDSR] = useState<Record<string, unknown> | null>(null);
  const [chips, setChips] = useState<PromptChip[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshDeals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.listDeals();
      setDeals(result);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load deals on mount
  useEffect(() => {
    refreshDeals();
  }, [refreshDeals]);

  const createDealFn = useCallback(
    async (params: Parameters<typeof api.createDeal>[0]) => {
      const deal = await api.createDeal(params);
      if (deal) {
        setDeals((prev) => [deal, ...prev]);
        setActiveDeal(deal);
      }
      return deal;
    },
    []
  );

  const updateDealFn = useCallback(
    async (id: string, fields: Partial<Pick<DealData, "status" | "title" | "notes" | "intended_use" | "property_type" | "county">>) => {
      const updated = await api.updateDeal(id, fields);
      if (updated) {
        setDeals((prev) =>
          prev.map((d) => (d.id === id ? { ...d, ...updated } : d))
        );
        setActiveDeal((prev) => (prev?.id === id ? { ...prev, ...updated } : prev));
      }
      return updated;
    },
    []
  );

  const handleDealContext = useCallback(
    (event: {
      deal_id: string;
      parcel_id?: string;
      address?: string;
      status: string;
      title?: string;
      completeness_score: number;
    }) => {
      // Update or add the deal to local state
      const dealData: DealData = {
        id: event.deal_id,
        parcel_id: event.parcel_id,
        address: event.address,
        status: event.status,
        title: event.title,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setActiveDeal(dealData);
      setDeals((prev) => {
        const idx = prev.findIndex((d) => d.id === event.deal_id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx]!, ...dealData };
          return next;
        }
        return [dealData, ...prev];
      });
      // Generate initial chips
      _updateChips(event.completeness_score, {});
    },
    []
  );

  const handleDSRUpdate = useCallback(
    (event: {
      deal_id: string;
      completeness_score: number;
      pir_status: Record<string, string>;
      ground_truth: Record<string, unknown>;
      estimates: Record<string, unknown>;
    }) => {
      setActiveDSR({
        deal_id: event.deal_id,
        completeness_score: event.completeness_score,
        pir_status: event.pir_status,
        ground_truth: event.ground_truth,
        estimates: event.estimates,
      });
      _updateChips(event.completeness_score, event.pir_status);
    },
    []
  );

  function _updateChips(completeness: number, pir: Record<string, string>) {
    const newChips: PromptChip[] = [];

    // Suggest based on gaps
    if (pir.tax_analysis !== "complete") {
      newChips.push({
        label: "Check taxes",
        message: "What would the taxes be if I bought this property?",
        category: "discovery",
      });
    }
    if (pir.site_screening !== "complete") {
      newChips.push({
        label: "Screen the site",
        message: "Screen this site for zoning, flood zone, and environmental issues",
        category: "discovery",
      });
    }
    if (pir.market_analysis !== "complete") {
      newChips.push({
        label: "Market snapshot",
        message: "What's the market like in this area?",
        category: "analysis",
      });
    }
    if (pir.owner_research !== "complete") {
      newChips.push({
        label: "Research owner",
        message: "Who owns this property and what else do they own?",
        category: "analysis",
      });
    }
    if (completeness >= 50) {
      newChips.push({
        label: "Run comps",
        message: "Find comparable sales near this property",
        category: "analysis",
      });
    }
    if (completeness >= 60) {
      newChips.push({
        label: "Synthesize",
        message: "Give me your overall assessment of this deal",
        category: "synthesis",
      });
    }

    setChips(newChips.slice(0, 4));
  }

  const handleChipsUpdate = useCallback(
    (serverChips: Array<{ label: string; message: string; category: string }>) => {
      setChips(serverChips.slice(0, 4) as PromptChip[]);
    },
    []
  );

  return (
    <DealCtx.Provider
      value={{
        deals,
        activeDeal,
        activeDSR,
        chips,
        loading,
        refreshDeals,
        setActiveDeal,
        setActiveDSR,
        createDeal: createDealFn,
        updateDeal: updateDealFn,
        handleDealContext,
        handleDSRUpdate,
        handleChipsUpdate,
      }}
    >
      {children}
    </DealCtx.Provider>
  );
}
